import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";
import Skip from "@/assets/icons/authentication/Skip.svg";

import DslrImage from "@/assets/images/auth/recording-cor-setup/dslr.png";
import IphoneImage from "@/assets/images/auth/recording-cor-setup/iphone.png";
import AndroidImage from "@/assets/images/auth/recording-cor-setup/android.png";
import TripodImage from "@/assets/images/auth/recording-cor-setup/tripod.png";
import MicrophoneImage from "@/assets/images/auth/recording-cor-setup/microphone.png";
import NoiseControlImage from "@/assets/images/auth/recording-cor-setup/noise-control-room.png";
import ArtificialLightingImage from "@/assets/images/auth/recording-cor-setup/artificial-light.png";
import AestheticImage from "@/assets/images/auth/recording-cor-setup/aesthetic-bg.png";
import GreenScreenImage from "@/assets/images/auth/recording-cor-setup/gree-screen.png";
import PlusImage from "@/assets/images/auth/recording-cor-setup/plus.png";

import {
  BODY_TYPE_OPTIONS,
  WHO_CAN_APPEAR_IN,
} from "@/constant/applicationFormConstant";

import {
  prevStep,
  nextStep,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import AuthOptionGroup, { AuthOptionItem } from "../../AuthOptionGroup";
import AuthInput from "../../AuthInput";
import { SkipToast } from "./Skiptoast";

type ApplicationStep5Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>; // keep prop, but unused
};

const ApplicationStep5 = ({
  currentStep,
  setCurrentStep,
}: ApplicationStep5Props) => {
  const dispatch = useAppDispatch();
  const [showSkipToast, setShowSkipToast] = useState(false);

  // Redux values
  const coreSetup = useAppSelector((s) => s.signUpV2.v2Extras.coreSetup);
  const whoCanAppear = useAppSelector((s) => s.signUpV2.v2Extras.whoCanAppear);
  const canBringToVideo = useAppSelector(
    (s) => s.signUpV2.v2Extras.canBringToVideo,
  );

  // Core setup options (you can add more later)
  const coreSetupOptions = useMemo(
    () =>
      [
        {
          type: "dslr",
          label: "DSLR Camera",
          icon: DslrImage,
          placeholder: "e.g. Canon M50, Sony A6400, Nikon D5600",
        },
        {
          type: "iphone",
          label: "Iphone",
          icon: IphoneImage,
          placeholder: "e.g. iPhone 13 / iPhone 14 / iPhone 15",
        },
        {
          type: "androidPhone",
          label: "Android Phone",
          icon: AndroidImage,
          placeholder: "e.g. Samsung S21, Pixel 7, OnePlus 11",
        },
        {
          type: "tripod",
          label: "Tripod / Stable mount",
          icon: TripodImage,
          placeholder: "e.g. Phone tripod, GorillaPod, Desk mount",
        },
        {
          type: "microphone",
          label: "External microphone",
          icon: MicrophoneImage,
          placeholder: "e.g. Boya M1, Rode Wireless GO, Built-in mic",
        },
        {
          type: "noise-control",
          label: "Quiet / noise-controlled room",
          icon: NoiseControlImage,
          placeholder: "e.g. Bedroom studio, Quiet home office",
        },
        {
          type: "artificial-lighting",
          label: "Artificial lighting",
          icon: ArtificialLightingImage,
          placeholder: "e.g. Ring light, Softbox, LED panel",
        },
        {
          type: "green-screen",
          label: "Green screen",
          icon: GreenScreenImage,
          placeholder: "e.g. Portable green screen, Full backdrop",
        },
        {
          type: "aesthetic-bg",
          label: "Aesthetic background",
          icon: AestheticImage,
          placeholder: "e.g. Minimal desk setup, Plant background, Studio wall",
        },
        {
          type: "others",
          label: "Others",
          icon: PlusImage,
          placeholder: "Any other equipment or setup details",
        },
      ] as const,
    [],
  );

  const isCoreSelected = (type: string) =>
    coreSetup.some((i) => i.type === type);

  const setCoreDetails = (type: string, details: string) => {
    const next = coreSetup.map((i) =>
      i.type === type ? { ...i, details } : i,
    );
    dispatch(updateV2Field("coreSetup", next));
  };

  const toggleCoreSetup = (type: string) => {
    const exists = coreSetup.some((i) => i.type === type);
    const next = exists
      ? coreSetup.filter((i) => i.type !== type)
      : [...coreSetup, { type, details: "" }];

    dispatch(updateV2Field("coreSetup", next));
  };

  // Who can appear (multi select)
  // AuthOptionGroup in multi mode expects AuthOptionItem[] in value/onChange
  const whoCanAppearValue: AuthOptionItem[] = useMemo(() => {
    const map = new Map(WHO_CAN_APPEAR_IN.map((o) => [o.key, o]));
    return whoCanAppear
      .map((k) => map.get(k))
      .filter(Boolean) as AuthOptionItem[];
  }, [whoCanAppear]);

  console.log(whoCanAppear);

  const handleWhoCanAppearChange = (items: AuthOptionItem[]) => {
    dispatch(
      updateV2Field(
        "whoCanAppear",
        items.map((i) => i.key),
      ),
    );
  };

  return (
    <>
      <div>
        {/* header */}
        <div className="mb-8">
          <div className="flex items-center gap-x-2 whitespace-nowrap">
            <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
              <p className="text-white text-[11px] leading-5 tracking-normal">
                Step 5 of 5
              </p>
            </div>
            <h1 className="text-[#060B13] text-base lg:text-xl tracking-[-0.7px] font-medium">
              Recording Setup & Equipment
            </h1>
          </div>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
            Select what you have access to. This helps brands match you with the
            right projects.
          </p>
        </div>

        <div className="space-y-8">
          {/* core setup */}
          <div>
            <label className="text-sm tracking-normal mb-2">
              <span className="text-[#121217] font-medium">Core Setup</span>{" "}
              <span className="text-[#475467]">
                (What can you record with?)
              </span>
            </label>

            {/* Chips (multi select) */}
            <div className="flex gap-2 flex-wrap mt-2">
              {coreSetupOptions.map((opt) => {
                const active = isCoreSelected(opt.type);
                return (
                  <button
                    key={opt.type}
                    type="button"
                    onClick={() => toggleCoreSetup(opt.type)}
                    className={`border py-[8.5px] px-[7px] rounded-lg flex items-center gap-x-1
                      ${active ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD] bg-white"}`}
                  >
                    <img src={opt.icon} alt={opt.label} className="size-5" />
                    <p className="text-xs tracking-[-0.5px]">{opt.label}</p>
                  </button>
                );
              })}
            </div>

            <label className="text-[#475467] text-[10px] tracking-normal">
              Select all that apply
            </label>

            {/* Details inputs for selected items */}
            <div className="mt-3 space-y-2.5">
              {coreSetup.map((item) => {
                const opt = coreSetupOptions.find((o) => o.type === item.type);
                if (!opt) return null;

                return (
                  <div
                    key={item.type}
                    className="flex items-center gap-2 rounded-lg border border-[#F2F4F7] bg-white px-2.5 py-1.5"
                  >
                    {/* Icon */}
                    <img
                      src={opt.icon}
                      alt={opt.label}
                      className="size-4 shrink-0 opacity-80"
                    />

                    {/* Label */}
                    <p className="text-[11px] font-medium text-[#475467] whitespace-nowrap">
                      {opt.label}
                    </p>

                    {/* Input */}
                    <input
                      type="text"
                      value={item.details}
                      onChange={(e) =>
                        setCoreDetails(item.type, e.target.value)
                      }
                      placeholder={opt.placeholder}
                      className="
              min-w-0
              flex-1
              bg-transparent
              text-[11px]
              text-[#1D2939]
              placeholder:text-[#98A2B3]
              outline-none
              border-none
              focus:ring-0
            "
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* who can appears (multi) */}
          <AuthOptionGroup
            label="Who can appear in your videos?"
            options={WHO_CAN_APPEAR_IN}
            multiple
            value={whoCanAppearValue}
            onChange={handleWhoCanAppearChange}
          />

          <AuthInput
            label="Tell us what you can bring to your video"
            name="address"
            placeholder="e.g. Professional lighting, acting experience, props, creative ideas..."
            value={canBringToVideo}
            onChange={(e) => {
              dispatch(updateV2Field("canBringToVideo", e.target.value));
            }}
          />

          <div className="border border-[#EDEDED] py-[7px] px-2 rounded-xl w-fit">
            <p className="text-[#475467] text-[10px] tracking-normal">
              😉 You can edit this anytime. Only relevant brands see your
              details.
            </p>
          </div>
        </div>

        {/* action button */}
        <div className="mt-[50px] flex items-center justify-end gap-3">
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              dispatch(prevStep());
              setCurrentStep((prev) => prev - 1);
            }}
          >
            <LeftArrowShort />
            Go Back
          </CustomButton>

          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              setShowSkipToast(true);
              setTimeout(() => {
                dispatch(nextStep());
                setCurrentStep((prev) => prev + 1);
              }, 2000);
            }}
          >
            <Skip />
            Skip
          </CustomButton>

          <CustomButton
            backgroundColor="#FF6600"
            fontSize="14px"
            fontWeight="600"
            color="white"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px]"
            onClick={() => {
              dispatch(nextStep());
              setCurrentStep((prev) => prev + 1);
            }}
          >
            Proceed
          </CustomButton>
        </div>
      </div>
      <SkipToast
        visible={showSkipToast}
        onClose={() => setShowSkipToast(false)}
      />
    </>
  );
};

export default ApplicationStep5;
